import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { logger } from "../utils/logger";

interface AIContextState {
  route: { path: string; params: Record<string, string>; query: Record<string, string> };
  page: { type: string | null; data: Record<string, unknown>; selections: unknown[]; filters: Record<string, unknown>; editingState: unknown; workflowStage: unknown };
  user: { role: string | null; permissions: string[]; preferences: Record<string, unknown> };
  workflow: { currentTask: unknown; completedSteps: unknown[]; nextSuggestions: string[]; contextualCommands: string[] };
  lastUpdated: number;
}

export interface AIContextValue {
  context: AIContextState;
  updatePageContext: (updates: Partial<AIContextState['page']>) => void;
  updateWorkflowContext: (updates: Partial<AIContextState['workflow']>) => void;
  registerPageState: (pageType: string, stateCallback: () => unknown) => void;
  unregisterPageState: (pageType: string) => void;
  getContextualSuggestions: () => string[];
}

const AIContext = createContext<AIContextValue | null>(null);

export { AIContext };

const createInitialContext = () => ({
  route: {
    path: '',
    params: {},
    query: {}
  },
  page: {
    type: null, // 'calendar' | 'notes' | 'patient' | 'billing' | 'admin'
    data: {},
    selections: [],
    filters: {},
    editingState: null,
    workflowStage: null
  },
  user: {
    role: null,
    permissions: [],
    preferences: {}
  },
  workflow: {
    currentTask: null,
    completedSteps: [],
    nextSuggestions: [],
    contextualCommands: []
  },
  lastUpdated: Date.now()
});

export function AIContextProvider({ children, user }: { children: React.ReactNode; user?: { role?: string; permissions?: string[]; preferences?: Record<string, unknown> } | null }) {
  const location = useLocation();
  const [aiContext, setAIContext] = useState(createInitialContext());
  const [pageStateCallbacks, setPageStateCallbacks] = useState({});

  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(x => x);
    const queryParams = new URLSearchParams(location.search);
    
    setAIContext(prev => ({
      ...prev,
      route: {
        path: location.pathname,
        params: pathSegments.reduce((acc, segment, index) => {
          acc[`segment${index}`] = segment;
          return acc;
        }, {}),
        query: Object.fromEntries(queryParams.entries())
      },
      page: {
        ...prev.page,
        type: detectPageType(location.pathname)
      },
      lastUpdated: Date.now()
    }));
  }, [location]);

  useEffect(() => {
    if (user) {
      setAIContext(prev => ({
        ...prev,
        user: {
          role: user.role,
          permissions: user.permissions || [],
          preferences: user.preferences || {}
        },
        lastUpdated: Date.now()
      }));
    }
  }, [user]);

  const detectPageType = (pathname: string) => {
    if (pathname.includes('/calendar')) return 'calendar';
    if (pathname.includes('/notes')) return 'notes';
    if (pathname.includes('/clients')) return 'patient';
    if (pathname.includes('/superbills') || pathname.includes('/payments')) return 'billing';
    if (pathname.includes('/admin')) return 'admin';
    return null;
  };

  const registerPageState = useCallback((pageType: string, stateCallback: () => unknown) => {
    setPageStateCallbacks(prev => ({
      ...prev,
      [pageType]: stateCallback
    }));
  }, []);

  const unregisterPageState = useCallback((pageType: string) => {
    setPageStateCallbacks(prev => {
      const newCallbacks = { ...prev };
      delete newCallbacks[pageType];
      return newCallbacks;
    });
  }, []);

  const updatePageContext = useCallback((updates: Partial<AIContextState['page']>) => {
    setAIContext(prev => ({
      ...prev,
      page: {
        ...prev.page,
        ...updates
      },
      lastUpdated: Date.now()
    }));
  }, []);

  const updateWorkflowContext = useCallback((updates: Partial<AIContextState['workflow']>) => {
    setAIContext(prev => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        ...updates
      },
      lastUpdated: Date.now()
    }));
  }, []);

  const getContextualSuggestions = useCallback(() => {
    const { page, route, workflow } = aiContext;
    const suggestions = [];

    const pageData = page.data as {
      selectedDate?: string;
      viewMode?: string;
      currentPatient?: string;
    };

    switch (page.type) {
      case 'calendar':
        if (pageData.selectedDate) {
          suggestions.push(`Schedule appointment for ${pageData.selectedDate}`);
        }
        if (pageData.viewMode === 'day') {
          suggestions.push('Show conflicts for today');
          suggestions.push('Block time slot');
        }
        suggestions.push('Show my schedule for this week');
        break;
        
      case 'notes':
        if (page.editingState) {
          suggestions.push('Continue this note');
          suggestions.push('Generate SOAP note');
        }
        if (pageData.currentPatient) {
          suggestions.push(`Show ${pageData.currentPatient}'s previous sessions`);
        }
        suggestions.push('Create new progress note');
        break;
        
      case 'patient':
        if (page.selections.length > 0) {
          suggestions.push('Schedule follow-up for selected patients');
          suggestions.push('Generate patient summary');
        }
        suggestions.push('Add new patient');
        suggestions.push('Show high-risk patients');
        break;
        
      case 'billing':
        suggestions.push('Generate superbills for this week');
        suggestions.push('Process pending payments');
        suggestions.push('Show revenue breakdown');
        break;
        
      case 'admin':
        suggestions.push('Show practice performance');
        suggestions.push('Generate compliance report');
        suggestions.push('Review system alerts');
        break;
    }

    return suggestions;
  }, [aiContext]);

  const value = {
    context: aiContext,
    updatePageContext,
    updateWorkflowContext,
    registerPageState,
    unregisterPageState,
    getContextualSuggestions
  };

  return (
    <AIContext.Provider value={value}>
      {children}
    </AIContext.Provider>
  );
}

export function useAIContext() {
  const context = useContext(AIContext);
  if (!context) {
    logger.warn('useAIContext must be used within an AIContextProvider');
    return {
      context: createInitialContext(),
      updatePageContext: () => {},
      updateWorkflowContext: () => {},
      registerPageState: () => {},
      unregisterPageState: () => {},
      getContextualSuggestions: () => []
    };
  }
  return context;
}

export function usePageContext(pageType: string) {
  const { registerPageState, unregisterPageState, updatePageContext } = useAIContext();
  
  useEffect(() => {
    return () => {
      unregisterPageState(pageType);
    };
  }, [pageType, unregisterPageState]);

  const updateContext = useCallback((updates: Partial<AIContextState['page']>) => {
    updatePageContext(updates);
  }, [updatePageContext]);

  return { updateContext };
}
